// The shell (slice 05, issue 745; design/07-ui-shell.md whole): the top bar
// with every feature behind a menu, three bars (the side bars holding tabs,
// qa-runs#404), the
// person's named drop-downs, presets per base-system label, the second
// window, the two resets and the offer to switch.
//
// The document is the person's own and lives on the server under the
// table's base-system label; this component loads it, hands the dock what
// this window showed last, persists every change a beat later, and tells any
// other window of the same person (a second window, another tab) that the
// document moved, so a preset applied here reaches there without a reload.
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  FIGHT_PRESET_ID, STOCK_PRESET_ID, VIEWS, type DeviceClass, type DockLayout, type LayoutDoc, type SlotId, type TableView, type UserSettings, type ViewId, type ViewState, emptyLayoutDoc, layoutKey,
} from '@dragons-table/vtt-shared';
import { boardBus } from '../board/bus';
import { copyLayout, deleteLayout, fetchLayout, saveLayout, saveSettings } from '../layout';
import { panelPrefs } from './panelPrefs';
import { StatBlockSettings } from './StatBlockSettings';
import { saveHistory } from '../history';
import { draftRecap, type RecapAnswer } from '../recap';
import { EXPORT_NOTE, downloadTable } from '../exportTable';
import { setDiagnostics, useDiagnosticsHash } from '../diagnostics';
import { capture } from '../diagnosticsBoot';
import { refusalWords, setBaseSystem, setSessionActive, useSessionState, type TableResult } from '../table';
import { ShellContext, ShellMenusContext, type ShellContextValue } from './context';
import {
  AddToSlotDialog, BaseSystemDialog, ColourDialog, TextDialog, ConfirmDialog, Modal, ViewAsDialog, MenusDialog, PresetsDialog, PromptDialog, SavePresetDialog, TabMenuDialog, ToolDialog,
} from './Dialogs';
import { Dock } from './Dock';
import { currentDeviceClass, usePhoneLayout, usePhoneScreen } from './device';
import { setLook } from '../board/tokenStatus';
import { ToolSheet } from './ToolSheet';
import { barOf, barsFromLayout, barsOfSlots, showingOf, slotsOf, stockSlots, type DockBars, type DockHandle, type ToolId } from './dockHandle';
import { fightLeftovers } from '../board/ClearFight';
import {
  addMenu, addToolToMenu, applyPreset, currentDock, deletePreset, listPresets, presetById, removeMenu, removeToolFromMenu, renameMenu,
  renamePreset, resetView, savePreset, withDock, withIcon, withTabName,
} from './model';
import { OfferBanner, PhoneNotice } from './Onboarding';
import { TopBar, sceneLine, type Menu, type MenuItem } from './TopBar';
import { GuestBanner, SLOT_LABELS, STANDARD_MENUS, toolById, toolsFor, type ToolDef } from './tools';
import { lookingThrough, useTableState } from '../table/TableState';
import { VIEW_ONLY_REASON, viewOnlyFor } from '../table/viewOnly';
import { selection } from '../board/selection';
import { focusTool } from '../board/focusTool';
import { measureTool } from '../board/measureTool';
import { whatsThisTool } from '../board/whatsThisTool';
import { fogTool } from '../board/fogTool';
import { zoneTool } from '../board/zoneTool';
import { wallTool } from '../board/wallTool';
import { gridEditor } from '../board/gridEditor';
import { GridSetup } from './panels/GridSetup';
import { areaTool } from '../board/areaTool';
import { drawTool } from '../board/drawTool';
import { addToEncounter } from './panels/PropertiesPanel';
import { lapsing } from './panels/Rulings';
import { SoundPlayer } from './SoundPlayer';
import { Announcer } from './Announcer';
import { combatantOf, displayColour, memberLabel, roleWord, type Power, defaultGrid, emptyEncounter, endTurn, type DmObject, type Encounter, type PlayerEncounter, type TableObject } from '@dragons-table/vtt-shared';
import { gridKept } from './gridKept';
import { RewindDialog } from './RewindDialog';
import { StartSessionDialog } from './StartSessionDialog';
import { RestDialog } from './RestDialog';
import { useStartEncounter } from './panels/startEncounter';
import { useUndo } from '../rewind';
import { takebacks } from '../takeback';
import { effectsSetting } from '../board/effects';
import { dice3dSetting } from '../board/DiceTray';
import { animateSetting } from '../board/animated';
import { sessionEnded, setTokens, siteTableUrl } from '../session';
import { track } from '../analytics';

export interface ShellProps {
  view: ViewId;
  campaignId: string;
  table: TableView;
  onResult: (r: TableResult) => void;
  board: ReactNode;
  settings: UserSettings;
  onSettings: (s: UserSettings) => void;
  /** The site's origin, for the campaign link. */
  siteUrl: string;
  /** How a second window is opened; the default is `window.open` on this table with its own socket (design/21 §5.4). */
  openWindow?: (url: string, name: string) => void;
  /** The class of screen (issue 1179); absent, read from this screen once (`currentDeviceClass`). */
  device?: DeviceClass;
  /** Is this a phone-sized screen (decision #1442); absent, read from this window on every resize (`usePhoneScreen`). */
  phone?: boolean;
}

type Dialog =
  | { kind: 'tool'; tool: ToolDef }
  | { kind: 'add'; slot: SlotId }
  | { kind: 'new-menu'; thenAdd?: ToolId }
  | { kind: 'save-preset' }
  | { kind: 'presets' }
  | { kind: 'menus' }
  | { kind: 'reset-view' }
  | { kind: 'reset-all' }
  | { kind: 'copy-layout' }
  | { kind: 'base-system' }
  | { kind: 'colour' }
  | { kind: 'text' }
  | { kind: 'view-as' }
  | { kind: 'end-session' }
  | { kind: 'session-off' }
  | { kind: 'rewind' }
  | { kind: 'start-session' }
  | { kind: 'export' }
  | { kind: 'rest' }
  | { kind: 'stat-block' }
  | { kind: 'tab'; tool: ToolId };

const SAVE_DELAY_MS = 400;

/** Two docks arrange the same windows the same way: which group is active is not part of the arrangement. */
export function sameArrangement(a: DockLayout | null, b: DockLayout | null): boolean {
  const strip = (d: DockLayout | null) => {
    if (!d) return null;
    const { activeGroup: _active, ...rest } = d as Record<string, unknown>;
    return JSON.stringify(rest);
  };
  return strip(a) === strip(b);
}
const CHANNEL = 'vtt-layout';

/** The text size and typeface on the document root (issue 1174); undone when the shell goes. */
export function useTextLook(size: number, face: 'standard' | 'reading'): void {
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = size === 100 ? '' : `${size}%`;
    if (face === 'reading') root.dataset.vttTypeface = 'reading'; else delete root.dataset.vttTypeface;
    return () => { root.style.fontSize = ''; delete root.dataset.vttTypeface; };
  }, [size, face]);
}

const defaultOpenWindow = (url: string, name: string) => { window.open(url, name); };

export function Shell({ view, campaignId, table, onResult, board, settings, onSettings, siteUrl, openWindow = defaultOpenWindow, device: deviceProp, phone: phoneProp }: ShellProps) {
  // The class of screen (issue 1179), read once: the stored layout it reads and writes, and the stock it starts from.
  const [device] = useState<DeviceClass>(() => deviceProp ?? currentDeviceClass());
  // A phone-sized screen (decision #1442) was told the table needs a tablet or larger; one in the phone layout (issue 1242)
  // no longer is. Read again on a resize: a window widened to a desktop's no longer keeps the phone's notice (issue 1242).
  const phoneSized = usePhoneScreen();
  const phone = phoneProp ?? phoneSized;
  // The phone layout (issue 1242), the main window's: the board alone on the screen, a side bar opened over it on demand.
  const phoneNow = usePhoneLayout();
  const phoneLayout = phoneNow && view === 'main';
  const [openBar, setOpenBar] = useState<'left' | 'right' | null>(null);
  const phoneRef = useRef(phoneLayout);
  phoneRef.current = phoneLayout;
  // The person's text size and typeface (issue 1174): the root font size and a mark on <html>, so every rem in the
  // shell — menus, panels, the log, the strip, dialogs, a menu drawn outside the shell — grows with it. This screen only.
  useTextLook(settings.textSize ?? 100, settings.typeface ?? 'standard');
  // The board's look (issues 1041, 1445), set here in the shell's own render, before any panel or the board renders:
  // a panel drawn before the board read the palette the board had not yet set, and kept the old one.
  setLook({ palette: settings.palette ?? 'standard', theme: settings.theme ?? 'dark', text: (settings.textSize ?? 100) / 100 });
  // The tabletop's build, shown small in the top bar (qa-runs 16): the deployed commit from its own health answer.
  const [build, setBuild] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    void fetch('/api/health').then((r) => (r.ok ? r.json() : null)).then((h: { commit?: unknown } | null) => {
      if (live && h && typeof h.commit === 'string' && /^[0-9a-f]{7,40}$/i.test(h.commit)) setBuild(h.commit.slice(0, 7));
    }).catch(() => {});
    return () => { live = false; };
  }, []);
  const meHandle = table.members.find((m) => m.id === table.me.id)?.handle ?? null;
  const label = layoutKey(table.baseSystem);
  // The label as a person reads it (issue 1332): the campaign's system code is shown as the system's name.
  const labelShown = label && label === table.campaignSystem && table.campaignSystemName ? table.campaignSystemName : label;
  const role = table.me.role;
  const stock = useMemo(() => stockSlots(view, device, role), [view, device, role]);
  // A tool opened from a menu on a tablet (issue 1179): its panel in a sheet over the board, not the bar chooser.
  const [sheet, setSheet] = useState<ToolDef | null>(null);
  const powers = table.me.powers ?? null;
  const [doc, setDoc] = useState<LayoutDoc | null>(null);
  const docRef = useRef<LayoutDoc>(emptyLayoutDoc());
  const handleRef = useRef<DockHandle | null>(null);
  // Each bar's tabs and the one showing (qa-runs#404); `slots` is the tool showing in each, which sizes the bars.
  const [bars, setBars] = useState<DockBars>(() => barsOfSlots(stock));
  const slots = useMemo(() => showingOf(bars), [bars]);
  const [dialog, setDialog] = useState<Dialog | null>(null);
  const [notice, setNotice] = useState('');
  // The session's recap (qa-runs 1164): where it was drafted, with its link, until dismissed.
  const [recap, setRecap] = useState<RecapAnswer | null>(null);
  // After a map change (design/08 §3, issue 1329): the grid stayed, and one click puts it back to the table's default.
  const [keptGrid, setKeptGrid] = useState(gridKept.get());
  useEffect(() => gridKept.subscribe(setKeptGrid), []);
  // The switch on offer: a preset, or (a fight over) the arrangement the main window had before it (qa-runs#404).
  const [offer, setOffer] = useState<{ id: string; back?: ViewState } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const sender = useRef(Math.random().toString(36).slice(2));
  const channel = useRef<BroadcastChannel | null>(null);
  // The Combat menu's actions (design/20 §3.2: one action, reachable from a top-bar menu): the table's state, the board's selection.
  const tableState = useTableState();
  // The scene the DM's window shows, named from the scenes list only the DM is sent (issue 1541); a player's line is the system.
  const sceneName = tableState.scene ? table.scenes?.find((x) => x.id === tableState.scene!.id)?.name ?? null : null;
  const campaignLine = sceneLine(sceneName, table.campaignSystemName ?? null);
  const encounter = (tableState.store.state.encounter ?? emptyEncounter()) as Encounter | PlayerEncounter;
  // Combat › Start encounter starts as the tracker and the strip do (issue 1425): sorted, and the unrolled asked about.
  const { request: startEncounter, prompt: startPrompt, cancel: cancelStart } = useStartEncounter(encounter as Encounter, tableState.store.state.objects as Record<string, TableObject>, table.members);
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => selection.get());
  useEffect(() => selection.subscribe(setSelectedIds), []);
  const [pointing, setPointing] = useState(() => focusTool.get().on);
  useEffect(() => focusTool.subscribe((d) => setPointing(d.on)), []);
  const [measuring, setMeasuring] = useState(() => measureTool.get().on);
  useEffect(() => measureTool.subscribe((r) => setMeasuring(r.on)), []);
  const [laying, setLaying] = useState(() => areaTool.get().on);
  // Visual effects (issue 1164): each person's own switch, kept in this browser.
  const [showFx, setShowFx] = useState(() => effectsSetting.get());
  useEffect(() => effectsSetting.subscribe(setShowFx), []);
  // 3D dice (issue 1183): off by default, each person's own; reduced motion keeps it off.
  const [dice3d, setDice3d] = useState(() => dice3dSetting.get());
  useEffect(() => dice3dSetting.subscribe(setDice3d), []);
  const [animate, setAnimate] = useState(() => animateSetting.get());
  useEffect(() => animateSetting.subscribe(setAnimate), []);
  useEffect(() => areaTool.subscribe((d) => setLaying(d.on)), []);
  const [drawing, setDrawing] = useState(() => drawTool.get().on);
  useEffect(() => drawTool.subscribe((d) => setDrawing(d.on)), []);
  // Looking through somebody's eyes ends drawing (the bug check of issue 1371): its strip and Escape are not there
  // while viewing, and the menu reads as the viewed person's, so a draw mode left on could not be seen or ended.
  useEffect(() => { if (tableState.viewingAs) drawTool.stop(); }, [tableState.viewingAs]);
  // A view-only table (issue 1383): past the DM's grace a player's stroke is refused, so a draw mode left open closes
  // when the table turns view-only, by the same test the server refuses with. The DM is never view-only.
  const drawLocked = viewOnlyFor(tableState.status.viewOnly, role, powers, 'draw.add');
  useEffect(() => { if (drawLocked) drawTool.stop(); }, [drawLocked]);
  // End my turn, likewise (issue 1583): greyed with the reason rather than refused once pressed.
  const endLocked = viewOnlyFor(tableState.status.viewOnly, role, powers, 'encounter.next');
  const [fogging, setFogging] = useState(() => fogTool.get().on);
  useEffect(() => fogTool.subscribe((d) => setFogging(d.on)), []);
  const [walling, setWalling] = useState(() => wallTool.get().on);
  useEffect(() => wallTool.subscribe((d) => setWalling(d.on)), []);
  const [explaining, setExplaining] = useState(() => whatsThisTool.get().on);
  useEffect(() => whatsThisTool.subscribe((d) => setExplaining(d.on)), []);
  const [zoning, setZoning] = useState(() => zoneTool.get().on);
  const debugHash = useDiagnosticsHash();
  useEffect(() => { capture('client.tool', { pointing, measuring, laying, drawing, fogging, walling, explaining, zoning }); }, [pointing, measuring, laying, drawing, fogging, walling, explaining, zoning]);
  useEffect(() => zoneTool.subscribe((d) => setZoning(d.on)), []);
  const [fittingGrid, setFittingGrid] = useState(() => gridEditor.get().setupOn);
  useEffect(() => gridEditor.subscribe((g) => setFittingGrid(g.setupOn)), []);
  const areas = Object.values(tableState.store.state.areas ?? {});
  const focuses = Object.values(tableState.store.state.focuses ?? {});
  const placing = tableState.store.state.placing === true;
  // Session Active? (qa-runs 1429): as the socket last said, else as the table's view read it.
  const sessionNews = useSessionState();
  const sessionOn = sessionNews ? sessionNews.active : table.sessionActive === true;
  const markSession = useCallback((active: boolean) => {
    void setSessionActive(campaignId, active).then((r) => { if (r.kind !== 'ok' && !sessionEnded()) setNotice(r.kind === 'refused' ? refusalWords(r) : 'Session Active? could not be changed just now.'); });
  }, [campaignId]);
  // Undo (issue 1169): the DM's newest change on this scene, while nobody else has changed it since — read again a beat
  // after anything moves, so the menu offers it only when the server would take it.
  // The grid's DM controls are the `map` power's (issue 1160), on the DM's board (the grid setup reads it), as `may` below.
  const mayGrid = role === 'dm' || (role === 'codm' && !!powers?.includes('map') && tableState.role === 'dm');
  const undo = useUndo(campaignId, tableState.scene?.id ?? null, tableState.store.n, role === 'dm');

  // ── The document, per label ───────────────────────────────────────────────
  useEffect(() => {
    let live = true;
    setDoc(null);
    fetchLayout(label, device).then((d) => {
      if (!live) return;
      docRef.current = d;
      setDoc(d);
      panelPrefs.load(d.panel);
      setBars(barsFromLayout(currentDock(d, view), stock));
    });
    return () => { live = false; };
  }, [label, view, device, stock]);

  // Which windows' arrangement the next write speaks for: this window's
  // always, the other's only when something here changed it (a preset
  // covering both, a reset). The server keeps the rest, so two windows of
  // the same person never overwrite each other's arrangement.
  const pendingViews = useRef<Set<ViewId>>(new Set([view]));

  const flush = useCallback(() => {
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    const views = [...pendingViews.current];
    pendingViews.current = new Set([view]);
    saveLayout(label, docRef.current, views, device).then((ok) => {
      if (ok) channel.current?.postMessage({ label, device, sender: sender.current });
      else if (!sessionEnded()) setNotice('Your arrangement could not be saved just now.');
    });
  }, [label, view, device]);

  /** Keep a new document: in state now, on the server a beat later. */
  const persist = useCallback((next: LayoutDoc) => {
    const prev = docRef.current;
    for (const v of VIEWS) if (JSON.stringify(prev.current[v]) !== JSON.stringify(next.current[v])) pendingViews.current.add(v);
    docRef.current = next;
    setDoc(next);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(flush, SAVE_DELAY_MS);
  }, [flush]);

  useEffect(() => () => { if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; } }, []);

  // The Properties panel's own settings (issue 1575) live in this document: a section opened or closed, a DM's stat
  // block regrouped, kept as the arrangement is.
  useEffect(() => {
    panelPrefs.onSave((panel) => persist({ ...docRef.current, panel }));
    return () => panelPrefs.onSave(null);
  }, [persist]);

  /** A document whose arrangement for THIS window may have changed: keep it, and reload the dock only if it did. */
  const applyDoc = useCallback((next: LayoutDoc) => {
    const before = JSON.stringify(currentDock(docRef.current, view));
    persist(next);
    const after = currentDock(next, view);
    if (JSON.stringify(after) !== before) {
      handleRef.current?.load(after);
      setBars(handleRef.current ? handleRef.current.bars() : barsFromLayout(after, stock));
    }
  }, [persist, view, stock]);

  // Another window of the same person moved the document (design/07 §6-§7).
  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') return undefined;
    const ch = new BroadcastChannel(CHANNEL);
    channel.current = ch;
    ch.onmessage = (e: MessageEvent<{ label?: string; device?: DeviceClass; sender?: string }>) => {
      // Another device class's layout is not this screen's (issue 1179): a desktop window and a tablet one never share it.
      if (e.data?.sender === sender.current || e.data?.label !== label || (e.data?.device ?? 'desktop') !== device) return;
      fetchLayout(label, device).then((fresh) => {
        const before = JSON.stringify(currentDock(docRef.current, view));
        docRef.current = fresh;
        setDoc(fresh);
        panelPrefs.load(fresh.panel);
        const mine = currentDock(fresh, view);
        if (JSON.stringify(mine) !== before) {
          handleRef.current?.load(mine);
          setBars(handleRef.current ? handleRef.current.bars() : barsFromLayout(mine, stock));
        }
      });
    };
    return () => { ch.close(); channel.current = null; };
  }, [label, view, device, stock]);

  useEffect(() => {
    if (!notice) return undefined;
    const t = setTimeout(() => setNotice(''), 5000);
    return () => clearTimeout(t);
  }, [notice]);

  // ── The dock ──────────────────────────────────────────────────────────────
  const onDockReady = useCallback((h: DockHandle) => {
    handleRef.current = h;
    setBars(h.bars());
  }, []);

  const onDockChange = useCallback((layout: DockLayout) => {
    // The phone layout's sizes are the phone's: only a change in what the bars hold is the person's own (issue 1242).
    if (phoneRef.current && JSON.stringify(barsFromLayout(layout, stock)) === JSON.stringify(barsFromLayout(currentDock(docRef.current, view), stock))) {
      if (handleRef.current) setBars(handleRef.current.bars());
      return;
    }
    // dockview reports a layout change for things that are not one — the
    // active group moving when someone clicks the board to move a token —
    // and each became a save (issue 793). Only a different arrangement is kept.
    if (sameArrangement(layout, currentDock(docRef.current, view))) {
      if (handleRef.current) setBars(handleRef.current.bars());
      return;
    }
    persist(withDock(docRef.current, view, layout));
    if (handleRef.current) setBars(handleRef.current.bars());
  }, [persist, view, stock]);

  // Into a used side bar a tool is a new tab beside what is there (qa-runs#404); the tool's own bar it leaves.
  const place = (slot: SlotId, tool: ToolId) => { const h = handleRef.current; h?.place(slot, tool); if (h) setBars(h.bars()); setDialog(null); if (phoneLayout) showBar(slot); };
  const closeTool = (tool: ToolId) => { const h = handleRef.current; h?.close(tool); if (h) setBars(h.bars()); setDialog(null); };
  /** A side bar opened over the board, in the phone layout (issue 1242): nothing else shows it. */
  const showBar = (slot: SlotId | null) => { if (phoneRef.current && (slot === 'left' || slot === 'right')) setOpenBar(slot); };
  /** The bar a tool sits in, showing or behind another tab. */
  const slotOf = (tool: ToolId): SlotId | null => barOf(bars, tool);

  // "Properties" from the token's menu (issue 812): where it already is; else on a free bar it may sit on; else the
  // tool's own dialog, so nothing the person arranged is replaced without asking.
  useEffect(() => boardBus.onShowTool((id) => {
    const tool = toolById(id);
    const h = handleRef.current;
    if (!tool || !h) return;
    track('tool-open', { tool: tool.id });
    const now = h.slots();
    // On a bar already: brought to the front if it was behind another tab (qa-runs#404).
    const on = barOf(h.bars(), id);
    if (on) { h.show(id); setBars(h.bars()); if (phoneRef.current && on !== 'bottom') setOpenBar(on as 'left' | 'right'); return; }
    const free = slotsOf(view).filter((k) => tool.slots.includes(k)).find((k) => !now[k]);
    if (free) { h.place(free, id); setBars(h.bars()); if (phoneRef.current && (free === 'left' || free === 'right')) setOpenBar(free); } else setDialog({ kind: 'tool', tool });
  }), [view]);

  // The × closes the tab (qa-runs#404): the last one leaves the empty bar with its +.
  const demote = useCallback((tool: ToolId) => {
    const h = handleRef.current;
    if (!h) return;
    h.close(tool);
    setBars(h.bars());
  }, []);
  const openAdd = useCallback((slot: SlotId) => setDialog({ kind: 'add', slot }), []);
  const openTabMenu = useCallback((tool: ToolId) => setDialog({ kind: 'tab', tool }), []);
  const icons = doc?.icons;
  const names = doc?.names;

  // ── Presets and the offer ─────────────────────────────────────────────────
  const switchTo = (presetId: string) => {
    const d = docRef.current;
    const preset = presetById(d, presetId);
    if (!preset) return;
    applyDoc(applyPreset(d, presetId));
    setOffer(null);
    if (!(view in preset.views)) setNotice(`"${preset.name}" covers only the ${view === 'main' ? 'second' : 'main'} window; nothing here changed.`);
    else setNotice(`Switched to "${preset.name}".`);
  };

  const offerPreset = useCallback((presetId: string) => {
    if (!settings.offerPresets) return;
    if (presetById(docRef.current, presetId)) setOffer({ id: presetId });
  }, [settings.offerPresets]);

  // A fight starting offers the Fight layout; ending, the layout from before it (qa-runs#404). The main window's
  // arrangement only — the Fight layout covers no other — and only on the change, never for a fight already running
  // when the table opens. Declined, nothing is offered back: nothing changed. Off with the Offer setting, as every offer.
  const running = encounter.running === true;
  const wasRunning = useRef<boolean | null>(null);
  const beforeFight = useRef<ViewState | null>(null);
  useEffect(() => {
    const was = wasRunning.current;
    wasRunning.current = running;
    if (was === null || was === running || view !== 'main') return;
    const mine = docRef.current.current.main;
    if (running) {
      if (mine.preset === FIGHT_PRESET_ID) return;
      beforeFight.current = JSON.parse(JSON.stringify(mine)) as ViewState;
      offerPreset(FIGHT_PRESET_ID);
      return;
    }
    const back = beforeFight.current;
    beforeFight.current = null;
    if (mine.preset !== FIGHT_PRESET_ID) { setOffer((o) => (o?.id === FIGHT_PRESET_ID ? null : o)); return; }
    if (!settings.offerPresets) return;
    setOffer(back ? { id: back.preset, back } : { id: STOCK_PRESET_ID });
  }, [running, view, offerPreset, settings.offerPresets]);

  /** Back to the arrangement from before the fight, exactly as it was (a changed stock layout stays changed). */
  const switchBack = (back: ViewState) => {
    const d = docRef.current;
    applyDoc({ ...d, current: { ...d.current, main: back } });
    setOffer(null);
    setNotice('Back to your layout from before the fight.');
  };

  const toggleOffer = async () => onSettings(await saveSettings({ offerPresets: !settings.offerPresets }));
  const togglePalette = async () => onSettings(await saveSettings({ palette: settings.palette === 'okabe-ito' ? 'standard' : 'okabe-ito' }));
  const toggleOutlines = async () => onSettings(await saveSettings({ outlines: settings.outlines === false }));
  const toggleStatusIcons = async () => onSettings(await saveSettings({ statusIcons: settings.statusIcons === false }));

  // ── The menus ─────────────────────────────────────────────────────────────
  const menus = useMemo<Menu[]>(() => {
    const d = doc ?? emptyLayoutDoc();
    // Viewing as somebody (issue 1354): the menus offer what THAT person has — none of the DM's own tools or acts —
    // with the way back where "View as…" stood. Back to your view brings everything back.
    const viewing = tableState.viewingAs;
    const tools = (viewing ? toolsFor(viewing.role) : toolsFor(role, powers)).filter((t) => table.practice || !t.practiceOnly);
    // A co-DM's DM tools (issue 1159): those their powers cover. The board's DM modes (fog, walls, zones, the grid, placing,
    // the order's controls) draw from the DM's board, so they need `see_hidden` too.
    const may = (need: Power, board = false) => !viewing && (role === 'dm' || (role === 'codm' && !!powers?.includes(need) && (!board || tableState.role === 'dm')));
    const toolItem = (t: ToolDef): MenuItem => ({ kind: 'tool', tool: t, placed: slotOf(t.id) });
    const act = (id: string, label: string, onSelect: () => void, extra: Partial<Extract<MenuItem, { kind: 'action' }>> = {}): MenuItem =>
      ({ kind: 'action', id, label, onSelect, ...extra });
    const extras: Record<string, MenuItem[]> = {
      table: [
        { kind: 'separator' },
        ...(viewing ? [
          act('view-as', 'Back to your view', () => tableState.viewAs(null)),
          // Theirs, shown as they have it; the DM's own colour is not changed from here.
          ...(viewing.role === 'player' ? [act('colour', 'Your colour…', () => undefined, { disabled: true })] : []),
        ] : role === 'dm' ? [act('view-as', 'View as…', () => setDialog({ kind: 'view-as' })), act('base-system', 'Base system…', () => setDialog({ kind: 'base-system' }))] : role === 'player' ? [act('colour', 'Your colour…', () => setDialog({ kind: 'colour' })),
          // Take back move (issue 1436): the player's latest move, asked of the DM; the server says when there is none.
          act('takeback', 'Take back move', () => { void takebacks.ask().then((words) => setNotice(words)); })] : []),
        // Placing the table (issue 811): what the DM moves or adds waits, unseen, until they are done.
        // The session ends (issue 1136): every ruling the DM did not keep lapses; a new session is what follows.
        ...(may('dice') ? [act('end-session', 'End the session…', () => setDialog({ kind: 'end-session' }))] : []),
        // Session Active? (qa-runs 1429): who may come in. Off, players and watchers wait outside; unticking with any of
        // them here asks first. By hand it posts nothing and closes no ruling. The DM's, and a co-DM's with End the session's right.
        ...(may('dice') ? [act('session-active', 'Session Active?', () => {
          if (!sessionOn) { markSession(true); return; }
          if (table.members.some((m) => (m.role === 'player' || m.role === 'watcher') && m.present)) setDialog({ kind: 'session-off' });
          else markSession(false);
        }, { checked: sessionOn })] : []),
        // Rest the party (issue 1482): only when the system has rest kinds; the DM's, or a co-DM's with Tokens (the site
        // rests a character for its party's DM and co-GMs alone).
        ...(may('tokens') && tableState.systemRules.rests.length ? [act('rest', 'Rest the party…', () => setDialog({ kind: 'rest' }))] : []),
        ...(may('tokens', true) ? [act('placing', 'Place the table (players see nothing until done)', () => tableState.act({ kind: 'placement.set', on: !placing }), { checked: placing })] : []),
        view === 'main'
          ? act('second-window', 'Open second window', () => openWindow(`${window.location.pathname}?view=second`, `vtt-second-${campaignId}`))
          : act('second-window', 'This is the second window', () => undefined, { disabled: true }),
        // A way out of a session left from another account (qa-runs 16), for everyone: the table's session goes, and the
        // site's door brings the person back as whoever is signed in on the site now.
        act('sign-out', 'Sign out of the table', () => { setTokens(null); openWindow(siteTableUrl(campaignId), '_self'); }),
        ...(role === 'dm' && !viewing ? [
          act('manage', 'Manage the campaign on the site', () => openWindow(`${siteUrl}/campaigns`, '_blank')),
          // Start session (qa-runs 1162): the session's start, and the group's Discord told when it is connected.
          act('start-session', 'Start session…', () => setDialog({ kind: 'start-session' })),
          // Every change with when, who and what — hidden things and private rolls included, so the DM's alone.
          // Undo and rewind (issue 1169): the DM's alone, never delegated.
          // Always on the menu (issue 1436), so the board's Undo button stands in its place: dimmed with nothing to undo.
          undo
            ? act('undo', `Undo: ${undo.summary.length > 60 ? `${undo.summary.slice(0, 57)}…` : undo.summary}`, () => tableState.act({ kind: 'scene.undo' } as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'That could not be undone just now.'); }))
            : act('undo', 'Undo: nothing to undo', () => undefined, { disabled: true }),
          act('rewind', 'Rewind the table…', () => setDialog({ kind: 'rewind' })),
          act('save-history', 'Save the table\'s history', () => { void saveHistory(campaignId, table.name).then((ok) => { if (!ok && !sessionEnded()) setNotice('The table\'s history could not be saved just now.'); }); }),
          // Exporting a table (issue 1170): scenes, maps, pictures, sounds, rules and kept rulings, as one file. The DM's alone.
          act('export', 'Download the table…', () => setDialog({ kind: 'export' })),
        ] : []),
      ],
      view: [
        { kind: 'separator' },
        // A tool that lives in the top bar (design/08 §4): the whole board on screen, one click.
        act('fit', 'Fit to screen', () => boardBus.fit(), { disabled: view !== 'main' }),
        // Focus highlights (design/08 §5): anyone points at a place; you clear your own, the DM clears anyone's.
        act('point', 'Point at a place', () => focusTool.toggle(), { checked: pointing, disabled: view !== 'main' }),
        act('clear-focuses', may('map') ? 'Clear every focus' : 'Clear my focuses', () => {
          if (may('map')) tableState.act({ kind: 'focus.clearAll' });
          else for (const f of focuses) if (f.ownerId === tableState.meId) tableState.act({ kind: 'focus.clear', id: f.id });
        }, { disabled: may('map') ? focuses.length === 0 : !focuses.some((f) => f.ownerId === tableState.meId) }),
        // Measuring and area templates (issue 832): a ruler of your own; areas everyone sees, cleared like focuses.
        // "What's this?" (issue 1155): the help control — a tap on a token opens its campaign wiki card.
        act('whats-this', "What's this?", () => whatsThisTool.toggle(), { checked: explaining, disabled: view !== 'main' }),
        act('effects', 'Show attack and spell effects', () => effectsSetting.set(!showFx), { checked: showFx }),
        act('dice3d', '3D dice', () => dice3dSetting.set(!dice3d), { checked: dice3d, disabled: dice3dSetting.reduced() }),
        // Animated tokens (issue 1176): off shows each one's first frame still.
        act('animate', 'Animate tokens', () => animateSetting.set(!animate), { checked: animate }),
        act('measure', 'Measure distance', () => measureTool.toggle(), { checked: measuring, disabled: view !== 'main' }),
        // A system with no distance rules has no areas (issue 1305): the tool is not offered, rather than refused.
        ...(tableState.systemRules.distance ? [act('area', 'Lay an area template', () => areaTool.toggle(), { checked: laying, disabled: view !== 'main' })] : []),
        act('clear-areas', may('map') ? 'Clear every area' : 'Clear my areas', () => {
          if (may('map')) tableState.act({ kind: 'area.clearAll' });
          else for (const a of areas) if (a.ownerId === tableState.meId) tableState.act({ kind: 'area.clear', id: a.id });
        }, { disabled: may('map') ? areas.length === 0 : !areas.some((a) => a.ownerId === tableState.meId) }),
        // Fog of war (issue 851): the DM's, a board mode like the others — the switch, reveal and cover sit over the board.
        ...(may('map', true) ? [act('fog', 'Fog of war', () => fogTool.toggle(), { checked: fogging, disabled: view !== 'main' || tableState.viewingAs !== null })] : []),
        // Walls (issue 1144): the DM's, a board mode like the fog — the chain is drawn on the board, the panel sits over it.
        ...(may('map', true) ? [act('walls', 'Draw walls', () => wallTool.toggle(), { checked: walling, disabled: view !== 'main' || tableState.viewingAs !== null })] : []),
        // Vision zones (issue 1149): the DM's, a board mode like the fog — a box of squares painted, the kind chosen over the board.
        ...(may('map', true) ? [act('zones', 'Paint zones', () => zoneTool.toggle(), { checked: zoning, disabled: view !== 'main' || tableState.viewingAs !== null })] : []),
        // Grid setup from the top bar (issue 1024): it lives in Properties > Scene, shown only with nothing selected, and
        // the playtest's DM never found it. This clears the selection, brings Properties up and turns setup on.
        ...(may('map', true) ? [act('grid', 'Grid setup', () => {
          if (gridEditor.get().setupOn) { gridEditor.setSetup(false); return; }
          selection.clear();
          gridEditor.setSetup(true);
          // Properties where it already is, or on a free bar it may sit on; with both bars full, the controls float over
          // the board in their own panel (QA retest of #1084: the bar chooser held none of them).
          const props = toolById('properties');
          const onBar = barOf(bars, 'properties') !== null;
          const free = props ? slotsOf(view).filter((k) => props.slots.includes(k)).find((k) => !slots[k]) : undefined;
          if (onBar || free) boardBus.showTool('properties');
        }, { checked: fittingGrid, disabled: view !== 'main' || tableState.viewingAs !== null })] : []),
      ],
      combat: (() => {
        const running = encounter.running;
        const objects = tableState.store.state.objects;
        const free = [...selectedIds].map((id) => objects[id]).filter((o): o is DmObject => Boolean(o) && o!.kind === 'token' && !combatantOf(encounter as Encounter, o!.id));
        const mine = 'active' in encounter && encounter.active ? encounter.combatants.find((c) => c.id === encounter.active)?.mine === true : false;
        const leftovers = may('tokens', true) ? fightLeftovers(encounter as Encounter, objects as Record<string, DmObject>).length : 0;
        return [
          { kind: 'separator' as const },
          // Drawing on the map (issue 1032, design/28 §4.2): a board mode, like laying an area; a watcher draws nothing.
          // Under View as (issue 1355), the viewed person's role decides it, as every other item here; and it reads as it
          // does for them (issue 1371), open to any player, while choosing it says the DM is only looking.
          // At a view-only table (issue 1383) it is greyed with the reason, by the test the server refuses a stroke with,
          // for whoever it reads as.
          ...((viewing ? viewing.role : role) !== 'watcher' ? (() => {
            const locked = viewing ? viewOnlyFor(tableState.status.viewOnly, viewing.role, null, 'draw.add') : drawLocked;
            return [act('draw', 'Draw on the map', () => { if (viewing) tableState.setNotice(lookingThrough(viewing)); else drawTool.toggle(); },
              { checked: drawing && !viewing && !locked, disabled: view !== 'main' || locked, ...(locked ? { reason: VIEW_ONLY_REASON } : {}) })];
          })() : []),
          ...(may('tokens', true) ? [
            act('add-selected', free.length ? `Add ${free.length === 1 ? free[0]!.name : `${free.length} selected`} to encounter` : 'Add selected to encounter', () => addToEncounter(tableState.act, free), { disabled: free.length === 0 }),
            running ? act('end-encounter', 'End encounter', () => tableState.act({ kind: 'encounter.end' })) : act('start-encounter', 'Start encounter', startEncounter, { disabled: encounter.combatants.length === 0 }),
            act('end-turn', 'End turn', () => { const b = endTurn(encounter); if (b) tableState.act(b); }, { disabled: !running }),
            // After the fight (issue 812): the same question End encounter asks, for a DM who kept the tokens at first.
            act('clear-fight', 'Clear the fight\'s tokens…', () => boardBus.requestClearFight(), { disabled: running || leftovers === 0 || view !== 'main' }),
          ] : [
            act('end-turn', 'End my turn', () => { const b = endTurn(encounter); if (b) tableState.act(b); },
              { disabled: !running || !mine || endLocked, ...(endLocked ? { reason: VIEW_ONLY_REASON } : {}) }),
          ]),
        ];
      })(),
      layout: [
        act('save-preset', 'Save as preset…', () => setDialog({ kind: 'save-preset' })),
        act('presets', 'Presets…', () => setDialog({ kind: 'presets' })),
        act('menus', 'Drop-downs…', () => setDialog({ kind: 'menus' })),
        act('new-menu', 'New drop-down…', () => setDialog({ kind: 'new-menu' })),
        act('offer', 'Offer to switch presets', () => { void toggleOffer(); }, { checked: settings.offerPresets }),
        // Identity colours and markers in a colour-blind-safe palette (issue 1041, A11Y-002): this person's screen only.
        act('palette', 'Colour-blind-safe colours', () => { void togglePalette(); }, { checked: settings.palette === 'okabe-ito' }),
        // The side outlines on the board (qa-runs#1157): on by default, this person's screen only, at every table.
        act('outlines', 'Side outlines', () => { void toggleOutlines(); }, { checked: settings.outlines !== false }),
        // Conditions and effects on tokens as pictures (qa-runs#1440): on by default; off, the text badges. This person's screen only.
        act('status-icons', 'Status icons on tokens', () => { void toggleStatusIcons(); }, { checked: settings.statusIcons !== false }),
        act('text', 'Text size and typeface…', () => setDialog({ kind: 'text' })),
        // How a creature's stat block is grouped in Properties (issue 1575, decision 3): the DM's own, display only.
        ...(may('tokens') ? [act('stat-block', 'Stat block sections…', () => setDialog({ kind: 'stat-block' }))] : []),
        { kind: 'separator' },
        act('reset-view', 'Reset this preset…', () => setDialog({ kind: 'reset-view' })),
        act('reset-all', 'Reset everything…', () => setDialog({ kind: 'reset-all' })),
        // Carry on across devices (issue 1182): the other class of screen's layout, here.
        act('copy-layout', device === 'tablet' ? 'Use my laptop layout on this tablet…' : 'Use my tablet layout on this computer…', () => setDialog({ kind: 'copy-layout' })),
      ],
    };
    const standard: Menu[] = STANDARD_MENUS.map((m) => ({
      id: m.id, label: m.label, items: [...tools.filter((t) => t.menu === m.id).map(toolItem), ...(extras[m.id] ?? [])],
    }));
    const custom: Menu[] = d.menus.map((m) => ({
      id: `custom:${m.id}`, label: m.name, custom: true,
      items: m.tools.map(toolById).filter((t): t is ToolDef => Boolean(t) && tools.some((x) => x.id === t!.id)).map(toolItem),
    }));
    return [...standard, ...custom];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc, role, slots, bars, view, settings.offerPresets, settings.palette, settings.outlines, settings.statusIcons, campaignId, table.name, table.practice, siteUrl, encounter, selectedIds, tableState.store.state.objects, tableState.act, tableState.systemRules.distance, pointing, focuses, tableState.meId, placing, measuring, explaining, laying, showFx, dice3d, areas, fogging, zoning, drawing, tableState.viewingAs, powers, tableState.role, undo, tableState.status.viewOnly, drawLocked, endLocked, sessionOn, markSession, table.members]);

  // The ⚙ on the Properties bar (issue 1575): the DM's, or a co-DM's with the tokens power, as the menu item is.
  const statBlockMine = !tableState.viewingAs && (role === 'dm' || (role === 'codm' && !!powers?.includes('tokens')));
  const openStatBlockSettings = useCallback(() => setDialog({ kind: 'stat-block' }), []);
  const ctx = useMemo<ShellContextValue>(() => ({ view, campaignId, table, onResult, board, openAdd, demote, settings, onSettings, device, phone: phoneLayout, stock, icons, names, openTabMenu, ...(statBlockMine ? { openStatBlockSettings } : {}) }),
    [view, campaignId, table, onResult, board, openAdd, demote, settings, onSettings, device, phoneLayout, stock, icons, names, openTabMenu, statBlockMine, openStatBlockSettings]);
  // A bar with nothing left on it has nothing to show, and off a phone nothing is opened over the board.
  useEffect(() => { if (openBar && (!phoneLayout || bars[openBar].tabs.length === 0)) setOpenBar(null); }, [openBar, phoneLayout, bars]);

  const currentPreset = doc ? presetById(doc, doc.current[view].preset) : null;
  const offered = offer && doc ? presetById(doc, offer.id) ?? (offer.back ? { id: offer.id, name: 'previous', views: {} } : null) : null;

  return (
    <ShellContext.Provider value={ctx}>
      <ShellMenusContext.Provider value={menus}>
      <div className="vtt-shell flex h-screen flex-col bg-stone-950 text-stone-100" data-testid="shell" data-view={view} data-vtt-theme={settings.theme === 'light' ? 'light' : 'dark'}>
        {/* A tool not on a bar opens where the person is (the sheet) on a tablet, and from their own named drop-down on
            any screen (issue 1291, Tom's A): the drop-down is a shortcut to the tool, not to its placement chooser. */}
        <TopBar menus={menus} siteUrl={siteUrl} phone={phoneLayout} onTool={(tool, menuId) => {
            track('tool-open', { tool: tool.id }); capture('client.panel', { panel: tool.id, menu: menuId });
            const on = slotOf(tool.id);
            // In the phone layout a tool on a side bar is opened there (issue 1242): the bar is out of sight, so a chooser would say nothing.
            if (phoneLayout && (on === 'left' || on === 'right')) { handleRef.current?.show(tool.id); if (handleRef.current) setBars(handleRef.current.bars()); setOpenBar(on); return; }
            if ((device === 'tablet' || phoneLayout || menuId.startsWith('custom:')) && !on) setSheet(tool); else setDialog({ kind: 'tool', tool });
          }}
          bars={phoneLayout ? <>
            {(['left', 'right'] as const).filter((slot) => bars[slot].tabs.length > 0).map((slot) => {
              const open = openBar === slot;
              const name = toolById(bars[slot].showing ?? bars[slot].tabs[0]!)?.title ?? SLOT_LABELS[slot];
              return (
                <button key={slot} type="button" aria-expanded={open} aria-label={open ? `Close the ${SLOT_LABELS[slot].toLowerCase()}` : `Open the ${SLOT_LABELS[slot].toLowerCase()}: ${name}`}
                  data-testid={`phone-bar-${slot}`} onClick={() => setOpenBar(open ? null : slot)}
                  className={`min-w-0 max-w-[7.5rem] truncate rounded-[3px] border px-2 py-1 text-xs ${open ? 'border-vtt-gold text-vtt-gold' : 'border-vtt-line text-vtt-soft'}`}>
                  {open ? '× Close' : name}
                </button>
              );
            })}
          </> : undefined}
          trailing={<>
            {/* The campaign in gold Cinzel, and under it the scene and the system (issue 1541): the scene's name is the
                DM's alone, from the scenes list only they are sent. */}
            <span className="flex min-w-0 flex-col items-end leading-tight">
              <span className="max-w-full truncate font-display text-[0.875rem] font-semibold tracking-[0.04em] text-vtt-gold" data-testid="table-name">{table.name}</span>
              {campaignLine && <span className="max-w-full truncate text-[0.71875rem] text-vtt-muted" data-testid="scene-line">{campaignLine}</span>}
            </span>
            {view === 'second' && <span className="shrink-0 rounded border border-stone-700 px-1.5 text-xs" data-testid="window-badge">Second window</span>}
            <span className="flex min-w-0 items-center gap-2 border-l border-vtt-line pl-4 text-xs" data-testid="arrival" title="Signed in to the table as this account. Table → Sign out of the table to change it.">
              {table.me.colour && <span aria-hidden="true" className="h-[0.5625rem] w-[0.5625rem] shrink-0 rounded-full" data-testid="arrival-colour" style={{ backgroundColor: displayColour(table.me.colour, settings.palette ?? 'standard', 'dark') }} />}
              {meHandle && <span className="min-w-0 truncate">@{meHandle}</span>}
              {meHandle && <span className="sr-only"> · </span>}
              <span className={table.me.role === 'dm' || table.me.role === 'codm' ? 'shrink-0 rounded-sm bg-vtt-gold px-1.5 py-px text-[0.625rem] font-semibold tracking-[0.08em] text-vtt-ink' : 'shrink-0'} data-testid="arrival-role">{roleWord(table.me.role)}</span>
            </span>
            {build && <a href="/api/health" target="_blank" rel="noreferrer" className="shrink-0 font-mono text-[0.625rem] text-vtt-muted hover:text-vtt-fg" data-testid="build" title="The tabletop's build: every screenshot carries it">build {build}</a>}
            {/* The session diagnostic log (qa-runs 1227): the DM's alone, off until turned on. The hash names this recording, to pass along in an issue. */}
            {role === 'dm' && !tableState.viewingAs && (
              <span className="flex shrink-0 flex-col items-end leading-tight text-[0.6875rem] text-vtt-muted" data-testid="debug-mode">
                {debugHash && <span className="select-text font-mono" data-testid="debug-hash">{debugHash}</span>}
                <label className="flex items-center gap-1">
                  <input type="checkbox" data-testid="debug-mode-toggle" checked={debugHash !== null}
                    onChange={(e) => { void setDiagnostics(campaignId, e.target.checked).then((done) => { if (!done && !sessionEnded()) setNotice('Debug mode could not be changed just now.'); }); }} />
                  Debug mode
                </label>
              </span>
            )}
          </>} />
        {keptGrid && mayGrid && (
          <p role="status" data-testid="grid-kept" className="flex flex-wrap items-center gap-2 border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm text-amber-300">
            The grid was kept from the last map.
            <button type="button" className="rounded border border-amber-600 px-2 py-0.5 text-xs hover:bg-stone-800"
              onClick={() => { tableState.act({ kind: 'grid.set', grid: defaultGrid() }); gridKept.dismiss(); }}>Reset grid</button>
            <button type="button" className="rounded border border-stone-600 px-2 py-0.5 text-xs hover:bg-stone-800" onClick={() => gridKept.dismiss()}>Keep it</button>
          </p>
        )}
        <GuestBanner campaignId={campaignId} me={table.me} members={table.members} />
        {view === 'main' && <PhoneNotice phone={phone && !phoneLayout} />}
        {/* The table's sound (issue 1161): played here whatever the dock holds; its one notice when the browser refuses. */}
        {view === 'main' && <SoundPlayer />}
        {/* What a screen reader is told (issue 1175): one region, in the main window only, so nothing is said twice. */}
        {view === 'main' && <Announcer />}
        {recap && (
          <p role="status" data-testid="recap-notice" className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm text-amber-300">
            <span>{recap.text}</span>
            {recap.url && <a href={recap.url} target="_blank" rel="noopener noreferrer" className="underline" style={{ minHeight: 44, display: 'inline-flex', alignItems: 'center' }} data-testid="recap-open">Open it.</a>}
            <button type="button" className="ml-auto text-stone-400 underline" style={{ minHeight: 44 }} onClick={() => setRecap(null)} data-testid="recap-dismiss">Dismiss</button>
          </p>
        )}
        {notice && <p role="status" data-testid="shell-notice" className="border-b border-stone-800 bg-stone-900 px-3 py-1 text-sm text-amber-300">{notice}</p>}
        {/* Grid setup with Properties on no bar (QA run 12 F1, and its retest): the controls float over the board in a
            panel of their own, the board still free for dragging lines. Done leaves grid setup, as closing Properties does. */}
        {mayGrid && view === 'main' && fittingGrid && !Object.values(slots).includes('properties') && (
          <section role="dialog" aria-label="Grid setup" data-testid="grid-setup-panel"
            className="fixed right-3 top-16 z-30 max-h-[75vh] w-80 overflow-y-auto vtt-popover p-2 text-sm shadow-xl">
            <div className="mb-1 flex items-center gap-2">
              <h3 className="vtt-title">Grid setup</h3>
              <button type="button" className="ml-auto rounded border border-stone-600 px-2 py-0.5 text-xs text-stone-100 hover:bg-stone-800" onClick={() => gridEditor.setSetup(false)}>Done</button>
            </div>
            <GridSetup />
          </section>
        )}
        {offered && offer && <OfferBanner preset={offered} back={!!offer.back} onSwitch={() => (offer.back ? switchBack(offer.back) : switchTo(offered.id))} onDismiss={() => setOffer(null)} />}
        {/* Its own stacking context (issue 1179): dockview's sashes are z-index 99, and unconfined a bar's edge lay over
            an open top-bar menu — a finger on a menu item took hold of the edge instead. */}
        <div className={`isolate min-h-0 flex-1 ${phoneLayout ? '[overflow:clip]' : ''}`} data-testid="dock-area">
          {doc ? (
            <Dock key={label} view={view} initial={currentDock(doc, view)} onReady={onDockReady} onChange={onDockChange} openBar={openBar} />
          ) : (
            <p className="p-4 text-sm text-stone-400" data-testid="layout-loading">Loading your arrangement…</p>
          )}
        </div>

        {sheet && (
          <ToolSheet tool={sheet} onPlace={() => { setDialog({ kind: 'tool', tool: sheet }); setSheet(null); }} onClose={() => setSheet(null)} />
        )}
        {dialog?.kind === 'tool' && doc && (
          <ToolDialog tool={dialog.tool} view={view} placed={slotOf(dialog.tool.id)} menus={doc.menus}
            onPlace={(slot) => place(slot, dialog.tool.id)}
            onRemove={() => closeTool(dialog.tool.id)}
            onToggleMenu={(menuId, add) => persist(add ? addToolToMenu(docRef.current, menuId, dialog.tool.id) : removeToolFromMenu(docRef.current, menuId, dialog.tool.id))}
            onNewMenu={() => setDialog({ kind: 'new-menu', thenAdd: dialog.tool.id })}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'add' && (
          <AddToSlotDialog slot={dialog.slot} tools={(tableState.viewingAs ? toolsFor(tableState.viewingAs.role) : toolsFor(role, powers)).filter((t) => table.practice || !t.practiceOnly)} held={bars[dialog.slot].tabs} onPick={(tool) => place(dialog.slot, tool)} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'new-menu' && (
          <PromptDialog title="New drop-down" label="Name" submitLabel="Create" maxLength={24}
            note="A drop-down on the top bar holding only what you put there. Naming it is what makes several of them work."
            onSubmit={(name) => {
              const made = addMenu(docRef.current, name);
              if (!made) return 'Give it a name you have not used.';
              persist(dialog.thenAdd ? addToolToMenu(made.doc, made.menu.id, dialog.thenAdd) : made.doc);
              setDialog(null);
              return null;
            }} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'save-preset' && doc && (
          <SavePresetDialog view={view} names={doc.presets.map((p) => p.name)}
            onSave={(name, views) => {
              const captured: Partial<Record<ViewId, DockLayout | null>> = {};
              for (const v of views) captured[v] = v === view ? (handleRef.current?.toJSON() ?? currentDock(docRef.current, v)) : currentDock(docRef.current, v);
              const saved = savePreset(docRef.current, name, captured);
              if (!saved) return 'That name cannot be used.';
              persist(saved.doc);
              setNotice(`Saved "${saved.preset.name}".`);
              setDialog(null);
              return null;
            }} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'presets' && doc && (
          <PresetsDialog presets={listPresets(doc)} current={{ main: doc.current.main.preset, second: doc.current.second.preset }}
            onSwitch={(id) => { switchTo(id); setDialog(null); }}
            onRename={(id, name) => { const next = renamePreset(docRef.current, id, name); if (!next) return 'Use a name you have not used.'; persist(next); return null; }}
            onDelete={(id) => persist(deletePreset(docRef.current, id))}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'menus' && doc && (
          <MenusDialog menus={doc.menus}
            onAdd={(name) => { const made = addMenu(docRef.current, name); if (!made) return 'Give it a name you have not used.'; persist(made.doc); return null; }}
            onRename={(id, name) => { const next = renameMenu(docRef.current, id, name); if (!next) return 'Use a name you have not used.'; persist(next); return null; }}
            onRemove={(id) => persist(removeMenu(docRef.current, id))}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'reset-view' && (
          <ConfirmDialog title="Reset this preset?" confirmLabel="Reset this window" cancelLabel="Cancel"
            body={<p>This puts the {view === 'main' ? 'main' : 'second'} window back to how "{currentPreset?.name ?? 'the stock layout'}" was saved. Your presets and drop-downs stay.</p>}
            onConfirm={() => { applyDoc(resetView(docRef.current, view)); setDialog(null); setNotice('Reset.'); }} onClose={() => setDialog(null)} />
        )}
        {startPrompt && <Modal title="Start the encounter" onClose={cancelStart} testId="start-encounter-dialog">{startPrompt}</Modal>}
        {dialog?.kind === 'start-session' && <StartSessionDialog campaignId={campaignId} onClose={() => setDialog(null)} onSaid={setNotice} />}
        {dialog?.kind === 'rewind' && (
          <RewindDialog campaignId={campaignId} sceneId={tableState.scene?.id ?? null} onClose={() => setDialog(null)}
            nameOf={(m) => (m.whoId ? memberLabel(table.members.find((x) => x.id === m.whoId), tableState.store.state.objects as never, { meId: tableState.meId, you: 'you' }) : m.who ? `@${m.who}` : null)}
            onRewind={(body) => tableState.act(body as never, (reply) => { if (!reply.ok) setNotice(reply.reason ?? 'The table could not be rewound just now.'); })} />
        )}
        {dialog?.kind === 'rest' && (
          <RestDialog campaignId={campaignId} sceneId={tableState.scene?.id ?? null} kinds={tableState.systemRules.rests} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'export' && (
          <ConfirmDialog title="Download the table" confirmLabel="Download" cancelLabel="Cancel"
            body={<p>{EXPORT_NOTE}</p>}
            onConfirm={() => {
              setDialog(null);
              void downloadTable(campaignId, table.name).then((got) => { if (got !== true && !sessionEnded()) setNotice(got); });
            }}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'session-off' && (
          <ConfirmDialog title="Untick Session Active?" confirmLabel="Send them to the waiting screen" cancelLabel="Keep the session active"
            body={<p>Players are at the table. Unticking sends every player and watcher back to the waiting screen until you tick it again. The session isn&apos;t ended and no ruling lapses.</p>}
            onConfirm={() => { markSession(false); setDialog(null); }}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'end-session' && (
          <ConfirmDialog title="End the session?" confirmLabel="End the session" cancelLabel="Keep playing"
            body={lapsing(tableState.rulings).length
              ? <div><p>These rulings were not kept, and lapse:</p><ul className="mt-1 list-disc pl-5">{lapsing(tableState.rulings).map((r) => <li key={r.id}>{r.text}</li>)}</ul><p className="mt-2">Kept rulings carry into the next session.</p></div>
              : <p>No ruling lapses: every ruling in force is kept.</p>}
            onConfirm={() => {
              const lapsed = lapsing(tableState.rulings).length;
              tableState.act({ kind: 'session.end' } as never, (reply) => {
                if (!reply.ok) { setNotice(reply.reason ?? 'The session could not be ended just now.'); return; }
                setNotice(lapsed ? `Session ended. ${lapsed === 1 ? 'One ruling' : `${lapsed} rulings`} lapsed; kept rulings carry into the next session.` : 'Session ended. Every ruling in force carries into the next session.');
                void draftRecap(campaignId).then((r) => { if (!sessionEnded()) setRecap(r); });
              });
              setDialog(null);
            }}
            onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'copy-layout' && (
          <ConfirmDialog title={device === 'tablet' ? 'Use your laptop layout here?' : 'Use your tablet layout here?'} confirmLabel="Use it here" cancelLabel="Cancel"
            body={<p>This replaces this screen's arrangement, presets and drop-downs for tables labelled "{labelShown || 'no system'}" with the ones you saved on your {device === 'tablet' ? 'laptop' : 'tablet'}. Your {device === 'tablet' ? 'laptop' : 'tablet'} keeps its own.</p>}
            onConfirm={() => {
              if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
              setDialog(null);
              void copyLayout(label, device === 'tablet' ? 'desktop' : 'tablet', device).then((r) => {
                if (!('doc' in r)) { setNotice(r.reason); return; }
                docRef.current = r.doc;
                setDoc(r.doc);
                panelPrefs.load(r.doc.panel);
                const mine = currentDock(r.doc, view);
                handleRef.current?.load(mine);
                setBars(handleRef.current ? handleRef.current.bars() : barsFromLayout(mine, stock));
                channel.current?.postMessage({ label, device, sender: sender.current });
                setNotice(device === 'tablet' ? 'Your laptop layout is on this tablet now.' : 'Your tablet layout is on this computer now.');
              });
            }} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'reset-all' && (
          <ConfirmDialog title="Reset everything?" confirmLabel="Reset everything" cancelLabel="Cancel" destructive
            body={<p>This forgets every preset and drop-down you have for tables labelled "{labelShown || 'no system'}" and returns both windows to the stock layout. There is no undo.</p>}
            onConfirm={() => {
              if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
              deleteLayout(label, device).then((ok) => { if (ok) channel.current?.postMessage({ label, device, sender: sender.current }); });
              const fresh = emptyLayoutDoc();
              docRef.current = fresh;
              setDoc(fresh);
              panelPrefs.load(undefined);
              handleRef.current?.load(null);
              setBars(handleRef.current ? handleRef.current.bars() : barsOfSlots(stock));
              setDialog(null);
              setNotice('Back to the stock layout.');
            }} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'base-system' && (
          <BaseSystemDialog value={table.baseSystem} system={{ code: table.campaignSystem, name: table.campaignSystemName }} onSet={async (value) => { onResult(await setBaseSystem(campaignId, value)); setDialog(null); }} onClose={() => setDialog(null)} />
        )}
        {dialog?.kind === 'colour' && <ColourDialog onClose={() => setDialog(null)} />}
        {dialog?.kind === 'text' && <TextDialog settings={settings} onChange={async (patch) => onSettings(await saveSettings(patch))} onClose={() => setDialog(null)} />}
        {dialog?.kind === 'view-as' && <ViewAsDialog onClose={() => setDialog(null)} />}
        {dialog?.kind === 'stat-block' && <StatBlockSettings campaignId={campaignId} onClose={() => setDialog(null)} />}
        {dialog?.kind === 'tab' && doc && (() => {
          const tool = toolById(dialog.tool);
          const slot = barOf(bars, dialog.tool);
          if (!tool || !slot) return null;
          const tabs = bars[slot].tabs;
          return (
            <TabMenuDialog tool={tool} slot={slot} index={tabs.indexOf(dialog.tool)} count={tabs.length} icon={doc.icons?.[dialog.tool] ?? null} name={doc.names?.[dialog.tool] ?? null}
              onMove={(where) => { const h = handleRef.current; if (!h) return; h.move(dialog.tool, where); setBars(h.bars()); if (where === 'other') setDialog(null); }}
              onIcon={(icon) => persist(withIcon(docRef.current, dialog.tool, icon as Parameters<typeof withIcon>[2]))}
              onName={(name) => persist(withTabName(docRef.current, dialog.tool, name))}
              onClose={() => closeTool(dialog.tool)} onDismiss={() => setDialog(null)} />
          );
        })()}
        <OfferHook offer={offerPreset} />
      </div>
      </ShellMenusContext.Provider>
    </ShellContext.Provider>
  );
}

/**
 * The seam a later slice calls to OFFER a switch (a fight starting, slice
 * 12): exposed on the window for now, since nothing in this build fires it.
 * It respects the person's setting and never switches on its own.
 */
declare global {
  interface Window { vttOfferPreset?: (presetId: string) => void }
}
function OfferHook({ offer }: { offer: (presetId: string) => void }) {
  useEffect(() => {
    window.vttOfferPreset = offer;
    return () => { delete window.vttOfferPreset; };
  }, [offer]);
  return null;
}
